import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Minus } from "lucide-react";
import { Reveal, SectionHead } from "./shared";
import { SERVICES } from "../../data/content";

const SERVICE_LINKS = [
    "/ai-receptionist",
    "/ai-receptionist",
    "/appointment-automation",
    "/lead-follow-up",
    "/lead-follow-up",
    "/business-automation",
    "/lead-follow-up",
    "/crm-automation",
    "/crm-automation",
    "/ai-receptionist",
];

const MiniChat = ({ example }) => (
    <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden"
    >
        <div className="mt-5 pt-5 border-t border-white/10 flex flex-col gap-2">
            {example.map((m, i) => {
                if (m.type === "system") {
                    return (
                        <span key={i} className="self-center ff-mono text-[9px] uppercase tracking-[0.15em] text-slate-500 bg-white/5 border border-white/10 rounded-full px-2.5 py-1">
                            {m.text}
                        </span>
                    );
                }
                if (m.type === "badge") {
                    return (
                        <span key={i} className="self-center inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2.5 py-1">
                            <Check className="w-2.5 h-2.5" />
                            {m.text}
                        </span>
                    );
                }
                const ai = m.from === "ai";
                return (
                    <motion.span
                        key={i}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + i * 0.12 }}
                        className={`max-w-[90%] px-3 py-2 text-[12px] leading-relaxed rounded-xl ${
                            ai
                                ? "self-end bg-gradient-to-br from-cyan-500/80 to-blue-600/80 text-white rounded-br-sm"
                                : "self-start bg-white/10 text-slate-300 rounded-bl-sm"
                        }`}
                    >
                        {m.text}
                    </motion.span>
                );
            })}
        </div>
    </motion.div>
);

export default function Services() {
    const [open, setOpen] = useState(null);

    return (
        <section id="automations" className="py-24 sm:py-32 relative" data-testid="services-section">
            <div className="container-x">
                <SectionHead
                    eyebrow="04 — Automations"
                    title="Your AI Employee Can Handle More Than Just Chat."
                    sub="Ten automations working together as one system — each one built around how your business actually runs."
                    testid="services-heading"
                />
                <div className="mt-16 grid md:grid-cols-2 gap-5">
                    {SERVICES.map((s, i) => {
                        const isOpen = open === i;
                        return (
                            <Reveal key={s.num} delay={(i % 2) * 0.08}>
                                <div
                                    className={`glass card-hover rounded-3xl p-7 h-full ${isOpen ? "border-cyan-400/30" : ""}`}
                                    data-testid={`service-card-${s.num}`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <span className="ff-mono text-xs text-cyan-400/70">{s.num}</span>
                                            <h3 className="mt-2 text-xl font-bold text-white tracking-tight">{s.title}</h3>
                                        </div>
                                        <button
                                            onClick={() => setOpen(isOpen ? null : i)}
                                            data-testid={`service-toggle-${s.num}`}
                                            aria-expanded={isOpen}
                                            className="shrink-0 w-9 h-9 rounded-full border border-white/15 bg-white/5 flex items-center justify-center text-slate-300 hover:border-cyan-400/50 hover:text-cyan-300 transition-[border-color,color] duration-300"
                                            aria-label={`See how ${s.title} works`}
                                        >
                                            {isOpen ? <Minus className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                                    <a
                                        href={SERVICE_LINKS[i]}
                                        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-300 hover:text-cyan-200 transition-colors duration-300"
                                        data-testid={`service-link-${s.num}`}
                                    >
                                        Learn more
                                        <ArrowRight className="w-3.5 h-3.5" />
                                    </a>
                                    <AnimatePresence>{isOpen && <MiniChat example={s.example} />}</AnimatePresence>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
